import { QuestionCircleOutlined } from '@ant-design/icons'
import { Col, Form, Input, Row, Switch, Tooltip } from 'antd'
import { IBaseFormProps } from 'Modules'
import { ERC_PERCENT_MAX_LENGTH } from 'constants/pto-v3'
import {
  ERC_ENABLED_FIELD,
  isValidErcPercent,
  normalizeErcPercentInput,
} from 'extends/pto-v3.extend'

type ErcField = {
  name: string
  label: string
  placeholder: string
  tooltip: string
}

const SELLING_COST_FIELD: ErcField = {
  name: 'ercSellingCost',
  label: 'Estimated Selling Cost',
  placeholder: 'Enter estimated selling cost',
  tooltip:
    'Estimated costs for the sale of property (Broker Fee, Tax, Legal Fee, Transaction Cost...)',
}

const GROWTH_RATE_FIELDS: ErcField[] = [
  {
    name: 'ercConservativeRate',
    label: 'Conservative Property Value Growth Rate',
    placeholder: 'Enter conservative growth rate',
    tooltip: 'Asset appreciation rate in a cautious market growth scenario',
  },
  {
    name: 'ercBaseRate',
    label: 'Base Property Value Growth Rate',
    placeholder: 'Enter base growth rate',
    tooltip: 'Asset appreciation rate in an expected market growth scenario',
  },
  {
    name: 'ercUpsideRate',
    label: 'Upside Property Value Growth Rate',
    placeholder: 'Enter upside growth rate',
    tooltip: 'Asset appreciation rate in a favorable market growth scenario',
  },
]

const FORMULAS = [
  'Property Return = Investment Amount × Property Value Growth Rate × (1 − Estimated Selling Cost)',
  'Staking Return = Investment Amount × Current Staking APR × Investment Horizon',
  'Total Expected Return = Property Return + Staking Return',
]

const ErcPercentItem = ({ field, disabled }: { field: ErcField; disabled: boolean }) => (
  <Form.Item
    label={
      <>
        {field.label}
        <Tooltip
          title={
            <div className="tooltip-box">
              <p className="tooltip-text">{field.tooltip}</p>
            </div>
          }
          placement="right"
        >
          <QuestionCircleOutlined className="tooltip-icon" />
        </Tooltip>
      </>
    }
    name={field.name}
    normalize={normalizeErcPercentInput}
    rules={[
      {
        required: true,
        whitespace: true,
        message: `Please input the ${field.label.toLowerCase()}!`,
      },
      {
        validator: (_, value) =>
          !value || isValidErcPercent(value)
            ? Promise.resolve()
            : Promise.reject(new Error('Invalid number, e.g. 12,5')),
      },
    ]}
  >
    <Input
      style={{ color: 'rgba(0, 0, 0, 0.85)' }}
      disabled={disabled}
      inputMode="decimal"
      maxLength={ERC_PERCENT_MAX_LENGTH}
      placeholder={field.placeholder}
      addonAfter="%"
    />
  </Form.Item>
)

/**
 * PTO v3 - Expected Return Calculator. Inputs used by the public website to compute the
 * expected return shown to investors; sent as `expectedReturnCalculator` on create / update.
 * Editable whenever the form is editable (no lock on deploy / selling status).
 */
const ExpectedReturnCalculator = ({ form, edit = true }: IBaseFormProps) => {
  const enabled = Form.useWatch(ERC_ENABLED_FIELD, form)

  return (
    <fieldset>
      <legend>Expected Return Calculator</legend>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          marginTop: -8,
          marginBottom: enabled ? 16 : 0,
        }}
      >
        <p style={{ margin: 0, color: 'rgba(0, 0, 0, 0.45)' }}>
          Used to calculate the expected return shown to investors on your public website.
        </p>
        <Form.Item name={ERC_ENABLED_FIELD} valuePropName="checked" initialValue={false} noStyle>
          <Switch disabled={!edit} />
        </Form.Item>
      </div>
      {enabled && (
        <>
          <Row gutter={48}>
            <Col span={12}>
              <ErcPercentItem field={SELLING_COST_FIELD} disabled={!edit} />
            </Col>
            <Col span={12} />
            {GROWTH_RATE_FIELDS.map((field) => (
              <Col span={12} key={field.name}>
                <ErcPercentItem field={field} disabled={!edit} />
              </Col>
            ))}
          </Row>

          <div
            style={{
              marginTop: 8,
              padding: '16px 20px',
              borderRadius: 8,
              background: '#f7f8fa',
              fontSize: 12,
              color: 'rgba(0, 0, 0, 0.65)',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8, color: 'rgba(0, 0, 0, 0.85)' }}>
              Calculation Formulas
            </div>
            {FORMULAS.map((formula) => (
              <div key={formula} style={{ lineHeight: '20px' }}>
                {formula}
              </div>
            ))}
          </div>
        </>
      )}
    </fieldset>
  )
}

export default ExpectedReturnCalculator
